import apiClient from './apiClient';

const billingService = {
    getSubscription: () => apiClient.get('/api/billing/subscription'),
    getUsage: () => apiClient.get('/api/billing/usage'),
    // Pass a string (legacy price id, must belong to a plan) or { planCode, interval }.
    createCheckout: (req) => apiClient.post('/api/billing/checkout',
      typeof req === 'string' ? { priceId: req } : req),
    createPortalSession: () => apiClient.post('/api/billing/portal'),
    getCurrentOrg: () => apiClient.get('/api/organizations/current'),
    updateOrg: (data) => apiClient.put('/api/organizations/current', data),
    getMembers: () => apiClient.get('/api/organizations/current/members'),
    inviteMember: (email, role) => apiClient.post('/api/organizations/current/members/invite', { email, role }),
    removeMember: (userId) => apiClient.delete(`/api/organizations/current/members/${userId}`),

    // Operator Panel endpoints (admin-only)
    listAllOrgs: () => apiClient.get('/api/organizations'),
    getOrgStats: () => apiClient.get('/api/organizations/stats'),
    getOrgById: (id) => apiClient.get(`/api/organizations/${id}`),
    updateOrgById: (id, data) => apiClient.put(`/api/organizations/${id}`, data),
    deleteOrg: (id) => apiClient.delete(`/api/organizations/${id}`),
    calculateDiskUsage: (id) => apiClient.post(`/api/organizations/${id}/disk-usage/calculate`),
    getOrgUsers: (id) => apiClient.get(`/api/organizations/${id}/users`),
    createOrg: (data) => apiClient.post('/api/organizations', data),
    createOrgUser: (orgId, data) => apiClient.post(`/api/organizations/${orgId}/users`, data),

    // Plans endpoints — REMOVED. The legacy `/api/plans` controller and
    // its `plans` table were never the authoritative plan store; the
    // operator UI for plans + the org-edit dropdown both go through
    // `services/plansApi.js` (`/api/v1.0/subscription-plans`).

    // Operators endpoints
    listOperators: () => apiClient.get('/api/operators'),
    createOperator: (data) => apiClient.post('/api/operators', data),
    updateOperator: (id, data) => apiClient.put(`/api/operators/${id}`, data),
    deleteOperator: (id) => apiClient.delete(`/api/operators/${id}`),
    /** Re-roll an operator's authenticator (clears 2FA → re-enroll next login). */
    resetOperatorMfa: (id) => apiClient.post(`/api/operators/${id}/reset-mfa`),

    // Website Operators (WEB_ADMIN sub-operator role) — scoped to the
    // default org's website surface (Waitlist, Documentation, Contact
    // Inbox). Created by SUPER_ADMIN; cannot reach Platform Controls.
    listWebsiteOperators: () => apiClient.get('/api/operators/website'),
    createWebsiteOperator: (data) => apiClient.post('/api/operators/website', data),
    updateWebsiteOperator: (id, data) => apiClient.put(`/api/operators/website/${id}`, data),
    deleteWebsiteOperator: (id) => apiClient.delete(`/api/operators/website/${id}`),
    /** Re-roll a website operator's authenticator (clears 2FA → re-enroll next login). */
    resetWebsiteOperatorMfa: (id) => apiClient.post(`/api/operators/website/${id}/reset-mfa`),
    /** Default-org users eligible for promotion to WEB_ADMIN. */
    listWebsiteOperatorCandidates: () => apiClient.get('/api/operators/website/candidates'),
    /** Upgrade an existing default-org user to a Website Operator. */
    promoteToWebsiteOperator: (userId) => apiClient.post(`/api/operators/website/promote/${userId}`),
    /** Strip the Website Operator role but keep the account. */
    demoteWebsiteOperator: (userId) => apiClient.post(`/api/operators/website/demote/${userId}`),

    // Captcha settings endpoints
    getCaptchaSettings: () => apiClient.get('/api/settings/captcha'),
    updateCaptchaSettings: (data) => apiClient.put('/api/settings/captcha', data),
    testCaptcha: () => apiClient.post('/api/settings/captcha/test'),

    // Audit log endpoints
    getAuditLogs: (params) => apiClient.get('/api/audit', { params }),
    exportAuditLogs: (params) => apiClient.get('/api/audit/export', { params, responseType: 'blob' }),

    // ClickHouse nodes endpoints
    listClickHouseNodes: () => apiClient.get('/api/infrastructure/clickhouse-nodes'),
    createClickHouseNode: (data) => apiClient.post('/api/infrastructure/clickhouse-nodes', data),
    updateClickHouseNode: (id, data) => apiClient.put(`/api/infrastructure/clickhouse-nodes/${id}`, data),
    setNodeDiskBudget: (id, gb) => apiClient.put(`/api/infrastructure/clickhouse-nodes/${id}/disk-budget`, { gb }),
    auditLog: (params = {}) => apiClient.get('/api/infrastructure/audit', { params }),
    clusterConsoleQuery: (clusterId, sql) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/console/query`, { sql }),
    retentionTables: () => apiClient.get('/api/infrastructure/tenant-retention/tables'),
    retentionListAll: () => apiClient.get('/api/infrastructure/tenant-retention'),
    retentionUpsert: (body) => apiClient.put('/api/infrastructure/tenant-retention', body),
    retentionDelete: (id) => apiClient.delete(`/api/infrastructure/tenant-retention/${id}`),
    retentionDryRun: (body) => apiClient.post('/api/infrastructure/tenant-retention/dry-run', body),
    retentionRunNow: (id) => apiClient.post(`/api/infrastructure/tenant-retention/${id}/run-now`),
    operationalHealth: () => apiClient.get('/api/infrastructure/clickhouse-operational-health'),
    alertRecipients: () => apiClient.get('/api/infrastructure/clickhouse-operational-health/recipients'),
    alertRecipientAdd: (email) => apiClient.post('/api/infrastructure/clickhouse-operational-health/recipients', { email }),
    alertRecipientToggle: (id, enabled) => apiClient.patch(`/api/infrastructure/clickhouse-operational-health/recipients/${id}`, { enabled }),
    alertRecipientDelete: (id) => apiClient.delete(`/api/infrastructure/clickhouse-operational-health/recipients/${id}`),
    alertRecipientTest: (email) => apiClient.post('/api/infrastructure/clickhouse-operational-health/recipients/test', { email }),
    cardinalityOverview: (lookbackDays = 0) => apiClient.get('/api/infrastructure/cardinality/overview', { params: { lookbackDays } }),
    cardinalityReport: (orgId, lookbackDays = 0) => apiClient.get(`/api/infrastructure/cardinality/report/${orgId}`, { params: { lookbackDays } }),
    cardinalityBackfill: (days = 7) => apiClient.post('/api/infrastructure/cardinality/backfill', null, { params: { days } }),
    consistencyReport: () => apiClient.get('/api/infrastructure/clickhouse-consistency'),
    schemaMigrationRecipe: (clusterId, database, table) =>
        apiClient.get(`/api/infrastructure/clickhouse-clusters/${clusterId}/schema-migration-recipe`, { params: { database, table } }),
    stateExport: () => apiClient.get('/api/infrastructure/clickhouse-state-export'),
    tenantMigrationRecipe: (destClusterId, sourceClusterId, orgId) =>
        apiClient.get(`/api/infrastructure/clickhouse-clusters/${destClusterId}/tenant-migration-recipe`,
            { params: { sourceId: sourceClusterId, orgId } }),
    deleteClickHouseNode: (id, cleanRemote = false) => apiClient.delete(`/api/infrastructure/clickhouse-nodes/${id}`, { params: { cleanRemote } }),
    deployClickHouseNode: (id, binaryId) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/deploy`, { binaryId }),
    startClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/start`),
    stopClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/stop`),
    restartClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/restart`),
    testSshClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/test-ssh`),
    healthCheckClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/health`),
    deepHealthCheckClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/deep-health`),
    preflightClickHouseNode: (id) => apiClient.post(`/api/infrastructure/clickhouse-nodes/${id}/preflight`),
    getNodeDeploymentLogs: (id) => apiClient.get(`/api/infrastructure/clickhouse-nodes/${id}/logs`),
    getAllDeploymentLogs: () => apiClient.get('/api/infrastructure/clickhouse-nodes/logs'),
    listClickHouseBinaries: () => apiClient.get('/api/infrastructure/clickhouse-nodes/binaries'),
    uploadClickHouseBinary: (formData) => apiClient.post(
      '/api/infrastructure/clickhouse-nodes/binaries',
      formData,
      // Disable axios default 30s timeout — a 700MB CH binary on a
      // home uplink is well past that. Server-side keepalive still
      // applies (nginx client_body_timeout + Spring multipart limits).
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
    ),
    deleteClickHouseBinary: (id) => apiClient.delete(`/api/infrastructure/clickhouse-nodes/binaries/${id}`),

    // ClickHouse cluster endpoints
    listClickHouseClusters: () => apiClient.get('/api/infrastructure/clickhouse-clusters'),
    createClickHouseCluster: (data) => apiClient.post('/api/infrastructure/clickhouse-clusters', data),
    updateClickHouseCluster: (id, data) => apiClient.put(`/api/infrastructure/clickhouse-clusters/${id}`, data),
    deleteClickHouseCluster: (id) => apiClient.delete(`/api/infrastructure/clickhouse-clusters/${id}`),
    assignNodeToCluster: (clusterId, data) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/assign-node`, data),
    removeNodeFromCluster: (clusterId, nodeId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/remove-node`, { nodeId }),
    configureCluster: (clusterId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/configure`),
    // Rewrites every MergeTree-family table in the cluster's database to
    // its Replicated*MergeTree counterpart over SSH. Async; track via the
    // cluster's statusMessage + the MIGRATE_TABLE_OK / FAIL deployment log.
    migrateClusterToReplicated: (clusterId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/migrate-to-replicated`),
    // Mark a cluster as the default landing zone for newly created
    // organizations. Exactly one cluster is flagged at a time —
    // setting one clears the flag from any other cluster.
    setDefaultCluster: (clusterId) => apiClient.post(`/api/infrastructure/tenant-clusters/default-cluster/${clusterId}`),
    validateClusterTopology: (clusterId) => apiClient.get(`/api/infrastructure/clickhouse-clusters/${clusterId}/validate-topology`),
    deployCluster: (clusterId, binaryId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/deploy`, { binaryId }),
    startCluster: (clusterId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/start`),
    stopCluster: (clusterId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/stop`),
    clusterHealthCheck: (clusterId) => apiClient.get(`/api/infrastructure/clickhouse-clusters/${clusterId}/health`),
    clusterHealthDetail: (clusterId) => apiClient.get(`/api/infrastructure/clickhouse-clusters/${clusterId}/health-detail`),
    drainCluster: (clusterId) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/drain`),
    gracefulStopCluster: (clusterId, force = false) => apiClient.post(`/api/infrastructure/clickhouse-clusters/${clusterId}/graceful-stop`, null, { params: { force } }),
    clusterTenantUsage: (clusterId) => apiClient.get(`/api/infrastructure/clickhouse-clusters/${clusterId}/tenant-usage`),
    getClusterNodes: (clusterId) => apiClient.get(`/api/infrastructure/clickhouse-clusters/${clusterId}/nodes`),
    getUnassignedNodes: () => apiClient.get('/api/infrastructure/clickhouse-clusters/unassigned-nodes'),

    // Tenant-Cluster assignment endpoints
    listTenantClusterAssignments: () => apiClient.get('/api/infrastructure/tenant-clusters'),
    assignTenantToCluster: (organizationId, clusterId) => apiClient.post('/api/infrastructure/tenant-clusters', { organizationId, clusterId }),
    unassignTenant: (organizationId) => apiClient.delete(`/api/infrastructure/tenant-clusters/${organizationId}`),
    getTenantsByCluster: (clusterId) => apiClient.get(`/api/infrastructure/tenant-clusters/by-cluster/${clusterId}`),
    getUnassignedTenants: () => apiClient.get('/api/infrastructure/tenant-clusters/unassigned'),
    getAvailableClustersForTenants: () => apiClient.get('/api/infrastructure/tenant-clusters/available-clusters'),
    updateClusterConnection: (clusterId, data) => apiClient.put(`/api/infrastructure/tenant-clusters/cluster-connection/${clusterId}`, data),
    testClusterConnection: (clusterId) => apiClient.post(`/api/infrastructure/tenant-clusters/test-connection/${clusterId}`),

    // ClickHouse Row Level Security provisioning (operator-only)
    getRlsStatus: () => apiClient.get('/api/v1.0/admin/clickhouse/rls-status'),
    provisionTenantRls: (orgId) => apiClient.post(`/api/v1.0/admin/clickhouse/orgs/${orgId}/provision-rls`),
    provisionAllRls: () => apiClient.post('/api/v1.0/admin/clickhouse/provision-all-rls'),

    // Cloud Synthetic Agents (operator-managed)
    cloudAgents: {
        list: () => apiClient.get('/api/operator/synthetics/agents'),
        create: (data) => apiClient.post('/api/operator/synthetics/agents', data),
        update: (id, data) => apiClient.put(`/api/operator/synthetics/agents/${id}`, data),
        delete: (id) => apiClient.delete(`/api/operator/synthetics/agents/${id}`),
        rotateKey: (agentId) => apiClient.post(`/api/operator/synthetics/agents/${agentId}/rotate-key`),
    },

    // Gateway management endpoints
    listGateways: () => apiClient.get('/api/operator/gateways'),
    getGateway: (id) => apiClient.get(`/api/operator/gateways/${id}`),
    registerGateway: (data) => apiClient.post('/api/operator/gateways', data),
    updateGateway: (id, data) => apiClient.put(`/api/operator/gateways/${id}`, data),
    rotateGatewayToken: (id) => apiClient.post(`/api/operator/gateways/${id}/rotate-token`),
    revokeGateway: (id) => apiClient.post(`/api/operator/gateways/${id}/revoke`),
    deleteGateway: (id) => apiClient.delete(`/api/operator/gateways/${id}`),
    getIngestionMetrics: (timeRange = '24h', orgId) => {
        const params = { timeRange };
        if (orgId) params.orgId = orgId;
        return apiClient.get('/api/operator/gateways/ingestion-metrics', { params });
    },
};

export default billingService;
